<template>
  <transition name="loading-fade">
    <div v-if="loading" class="global-loading-container" role="progressbar" aria-busy="true" aria-label="加载中">
      <div class="loading-backdrop"></div>
      <div class="loading-spinner">
        <div class="spinner-wrapper">
          <el-icon :size="spinnerSize" class="spinning-icon">
            <Loading /></el-icon>
        </div>
        <p v-if="loadingText" class="loading-text">{{ loadingText }}</p>
        <div class="loading-indicator" ref="loadingIndicator"></div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useAppStore } from '@/stores/app';
import { Loading } from '@element-plus/icons-vue';

const appStore = useAppStore();
const loading = computed(() => appStore.isLoading);
const loadingText = computed(() => appStore.loadingText);
const loadingIndicator = ref<HTMLElement>();

// 根据屏幕尺寸自适应调整spinner大小
const spinnerSize = computed(() => {
  const isMobile = window.innerWidth < 768;
  return isMobile ? 36 : 48;
});

// 监听窗口大小变化
const handleResize = () => {
  // 重新计算spinner大小会通过computed自动更新
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped lang="scss">


.global-loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: $z-index-loading;
  pointer-events: none;
}

.loading-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(#000, 0.5);
  backdrop-filter: blur(2px);
  pointer-events: all;
}

.loading-spinner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: $bg-color-card;
  padding: $spacing-xl;
  border-radius: $border-radius-large;
  box-shadow: $box-shadow-dark;
  min-width: 120px;
  pointer-events: none;
  // 添加轻微的呼吸效果
  animation: pulse-subtle 2s ease-in-out infinite;
  
  // 响应式调整
  @media (min-width: #{$breakpoint-md}) {
    padding: $spacing-lg;
    min-width: 100px;
  }
}

.spinner-wrapper {
  position: relative;
  // 添加辉光效果的伪元素
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background-color: $primary-color;
    border-radius: 50%;
    opacity: 0.2;
    animation: glow-pulse 2s ease-in-out infinite;
  }
}

.spinning-icon {
  color: $primary-color;
  animation: spin 1.5s linear infinite;
  position: relative;
  z-index: 1;
}

.loading-text {
  margin-top: $spacing-md;
  color: $text-regular;
  font-size: $font-size-base;
  font-weight: $font-weight-medium;
  text-align: center;
  line-height: 1.5;
  max-width: 200px;
  @media (min-width: #{$breakpoint-md}) {
    font-size: $font-size-small;
    max-width: 160px;
  }
}

.loading-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, $primary-color, color-mix(in srgb, $primary-color, white 20%));
  border-radius: 0 0 $border-radius-large $border-radius-large;
  animation: progress-loading 1.5s ease-in-out infinite;
}

// 动画定义
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse-subtle {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes glow-pulse {
  0%, 100% {
    opacity: 0.1;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.2;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

@keyframes progress-loading {
  0% {
    width: 0;
    left: 0;
  }
  50% {
    width: 50%;
    left: 0;
  }
  100% {
    width: 100%;
    left: 0;
  }
}

// 过渡动画
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: all $transition-duration-base $transition-timing-base;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}

.loading-fade-enter-from .loading-spinner,
.loading-fade-leave-to .loading-spinner {
  transform: scale(0.8);
  opacity: 0;
}

// 无障碍访问优化
@media (prefers-reduced-motion: reduce) {
  .loading-spinner,
  .spinner-wrapper::before,
  .loading-indicator {
    animation: none !important;
  }
  
  .spinning-icon {
    animation: spin 4s linear infinite; // 减少动画速度
  }
  
  .loading-fade-enter-active,
  .loading-fade-leave-active {
    transition: opacity 0.5s ease;
  }
}

// 高对比度模式支持
@media (prefers-contrast: high) {
  .loading-backdrop {
    background-color: rgba(#000, 0.8);
  }
  
  .loading-spinner {
    border: 2px solid $text-primary;
  }
  
  .spinning-icon {
    color: $text-primary;
  }
}
</style>